import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { TagInput } from '@/components/ui/tag-input';
import { DOCUMENT_TAG_SUGGESTIONS } from '@constants/documentMetadata';
import { MetadataSection } from './MetadataSection';

interface MetadataTagsSectionProps {
  tags: string[];
  error?: string;
  onTagsChange: (tags: string[]) => void;
  onAddTag: (tag: string) => boolean;
}

const MetadataTagsSection: React.FC<MetadataTagsSectionProps> = ({
  tags,
  error,
  onTagsChange,
  onAddTag,
}) => {
  const { t } = useTranslation();
  const [newTag, setNewTag] = React.useState('');

  const addCustomTag = () => {
    if (onAddTag(newTag)) setNewTag('');
  };

  return (
    <MetadataSection title={t('metadata.tags')}>
      <Field label={t('metadata.tags')} help={t('metadata.tagsHelp')} error={error}>
        {(control) => (
          <TagInput
            {...control}
            value={tags}
            onChange={onTagsChange}
            suggestions={DOCUMENT_TAG_SUGGESTIONS}
            placeholder={t('metadata.tagsPlaceholder')}
            addLabel={t('metadata.tags')}
            removeLabel={t('common.delete')}
          />
        )}
      </Field>

      <div className="flex items-end gap-3">
        <Field label={t('metadata.addCustomTag')} className="max-w-64 flex-1">
          {(control) => (
            <Input
              {...control}
              value={newTag}
              onChange={(event) => setNewTag(event.target.value)}
              onKeyDown={(event) => {
                // isComposing keeps the Enter that confirms a kana→kanji
                // conversion from committing a half-typed tag.
                if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
                event.preventDefault();
                addCustomTag();
              }}
            />
          )}
        </Field>

        <Button type="button" variant="outline" onClick={addCustomTag} disabled={!newTag.trim()}>
          <Plus className="size-4" strokeWidth={2} />
          {t('metadata.add')}
        </Button>
      </div>
    </MetadataSection>
  );
};

export { MetadataTagsSection };
